import React, { useState, useEffect } from 'react';
import { Plus, Send, Download, Eye, Check, X, AlertCircle, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/use-toast';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, payment: null });
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/finance/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load payments. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendConfirmation = async (payment) => {
    setSendingConfirmation(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/finance/payments/${payment.id}/send-confirmation`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Payment confirmation sent successfully.",
          duration: 5000,
        });
        
        // Refresh payments to get updated receipt number
        await fetchPayments();
      } else {
        throw new Error(response.data.message || 'Failed to send confirmation');
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to send confirmation. Please try again.",
        duration: 5000,
      });
    } finally {
      setSendingConfirmation(false);
      setConfirmDialog({ open: false, payment: null });
    }
  };

  const handleDownloadReceipt = (receiptNumber) => {
    const token = localStorage.getItem('admin_token');
    const downloadUrl = `${API_URL}/api/finance/receipts/${receiptNumber}/download`;
    
    // Open in new tab with auth token
    window.open(`${downloadUrl}?token=${token}`, '_blank');
  };

  const handlePreviewReceipt = (receiptNumber) => {
    const token = localStorage.getItem('admin_token');
    const previewUrl = `${API_URL}/api/finance/receipts/${receiptNumber}/preview`;
    
    // Open in new tab
    window.open(`${previewUrl}?token=${token}`, '_blank');
  };

  const getStatusBadge = (payment) => {
    const status = payment.status || 'paid';
    
    const statusConfig = {
      paid: { label: 'Paid', variant: 'default', color: 'bg-green-100 text-green-800 border-green-300' },
      pending: { label: 'Pending', variant: 'secondary', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      failed: { label: 'Failed', variant: 'destructive', color: 'bg-red-100 text-red-800 border-red-300' }
    };

    const config = statusConfig[status] || statusConfig.paid;

    return (
      <Badge className={`${config.color} border font-medium`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-600 mt-1">Track and manage payments</p>
        </div>
        <Button className="bg-[#00B4D8] hover:bg-[#0096c7] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Payments List */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>All recorded payments with confirmation status</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No payments recorded</h3>
              <p className="text-gray-600 mb-4">Start recording payments to track your cash flow</p>
              <Button className="bg-[#00B4D8] hover:bg-[#0096c7] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#023E8A]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Invoice</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Method</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Receipt</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment, index) => (
                    <tr 
                      key={payment.id} 
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                      onClick={() => navigate(`/admin/finance/payments/${payment.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(payment.payment_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#023E8A]">
                        #{payment.invoice_number || payment.invoice_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {payment.payment_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {payment.currency} {payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(payment)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {payment.receipt_number ? (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#00B4D8] hover:text-[#0096c7] hover:bg-blue-50"
                              onClick={() => handlePreviewReceipt(payment.receipt_number)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-[#023E8A] hover:text-[#012a5e] hover:bg-blue-50"
                              onClick={() => handleDownloadReceipt(payment.receipt_number)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                        {payment.receipt_number ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#00B4D8] text-[#00B4D8] hover:bg-blue-50"
                            onClick={() => setConfirmDialog({ open: true, payment })}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Resend
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                            onClick={() => setConfirmDialog({ open: true, payment })}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Send Confirmation
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, payment: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#023E8A] text-xl font-semibold">
              Send Payment Confirmation
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base leading-relaxed">
              This will send a confirmation email with the receipt PDF to the client.
              {confirmDialog.payment?.receipt_number && (
                <span className="block mt-2 text-sm text-[#00B4D8]">
                  Receipt #{confirmDialog.payment.receipt_number} will be attached.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {confirmDialog.payment && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 my-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Invoice:</span>
                <span className="font-semibold text-gray-900">
                  #{confirmDialog.payment.invoice_number || confirmDialog.payment.invoice_id}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold text-gray-900">
                  {confirmDialog.payment.currency} {confirmDialog.payment.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Method:</span>
                <span className="font-semibold text-gray-900">{confirmDialog.payment.payment_method}</span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, payment: null })}
              disabled={sendingConfirmation}
              className="border-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSendConfirmation(confirmDialog.payment)}
              disabled={sendingConfirmation}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {sendingConfirmation ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
